import { useState, useEffect } from 'react'
import Header from './components/Header'
import ProductGrid from './components/ProductGrid'
import Cart from './components/Cart'
import ChatWidget from './components/ChatWidget'
import { Product, CartItem } from './types'

const API_URL = import.meta.env.VITE_API_URL || 'https://store.robdanz.com/api'

// Sample products as fallback if API fails
const SAMPLE_PRODUCTS: Product[] = [
  { id: '1', name: 'ProBook 15" M3', description: 'High-performance laptop with M3 chip, 16GB RAM, 512GB SSD', price: 1299.99, image_url: '', category: 'laptops', stock: 25, sku: 'LAPTOP-001' },
  { id: '2', name: 'UltraBook Air', description: 'Ultra-light laptop with Intel Core i7, 16GB RAM, 256GB SSD', price: 999.99, image_url: '', category: 'laptops', stock: 30, sku: 'LAPTOP-002' },
  { id: '5', name: 'Galaxy Pro 25', description: 'Latest flagship with 256GB storage, 50MP camera', price: 899.99, image_url: '', category: 'phones', stock: 40, sku: 'PHONE-001' },
  { id: '6', name: 'iPhone 16 Pro', description: 'A18 Pro chip, 128GB storage, titanium design', price: 1099.99, image_url: '', category: 'phones', stock: 35, sku: 'PHONE-002' },
  { id: '9', name: 'iPad Pro 12.9"', description: 'M4 chip, 256GB storage, Liquid Retina XDR display', price: 1199.99, image_url: '', category: 'tablets', stock: 20, sku: 'TABLET-001' },
  { id: '12', name: 'NoiseCancel Pro', description: 'Premium noise-canceling headphones with 40-hour battery', price: 349.99, image_url: '', category: 'headphones', stock: 45, sku: 'AUDIO-001' },
  { id: '16', name: 'USB-C Hub Pro', description: '7-in-1 hub with HDMI, USB-A, SD card reader', price: 79.99, image_url: '', category: 'accessories', stock: 60, sku: 'ACC-001' },
  { id: '18', name: 'Mechanical Keyboard', description: 'RGB mechanical keyboard with hot-swappable switches', price: 129.99, image_url: '', category: 'accessories', stock: 40, sku: 'ACC-002' },
]

function App() {
  const [products, setProducts] = useState<Product[]>([])
  const [cart, setCart] = useState<CartItem[]>([])
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [usingFallback, setUsingFallback] = useState(false)

  useEffect(() => {
    fetchProducts()
    const savedCart = localStorage.getItem('cart')
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart))
      } catch (e) {
        console.error('Failed to parse cart:', e)
      }
    }
  }, [])

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart))
  }, [cart])

  const fetchProducts = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Try to fetch from API
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)
      
      const response = await fetch(`${API_URL}/products`, {
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      
      const data = await response.json()
      const productsArray = data.products || data
      
      if (Array.isArray(productsArray) && productsArray.length > 0) {
        setProducts(productsArray)
        setUsingFallback(false)
      } else {
        throw new Error('No products returned')
      }
    } catch (err) {
      console.warn('API failed, using fallback products:', err)
      setProducts(SAMPLE_PRODUCTS)
      setUsingFallback(true)
      setError('Using offline mode - API unavailable')
    } finally {
      setLoading(false)
    }
  }

  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id)
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      }
      return [...prev, { ...product, quantity: 1 }]
    })
  }

  const removeFromCart = (productId: string) => {
    setCart(prev => prev.filter(item => item.id !== productId))
  }

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId)
      return
    }
    setCart(prev =>
      prev.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    )
  }

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0)
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading store...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        cartCount={cartCount} 
        onCartClick={() => setIsCartOpen(true)} 
      />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Cloudflare Demo Store</h1>
          <p className="text-gray-600 mt-2">
            Built with Cloudflare Workers, D1, KV, and Durable Objects
          </p>
          {usingFallback && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
              ⚠️ {error} - Showing sample products
            </div>
          )}
        </div>

        <ProductGrid products={products} onAddToCart={addToCart} />
      </main>

      <Cart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        onUpdateQuantity={updateQuantity}
        onRemove={removeFromCart}
        total={cartTotal}
      />

      <ChatWidget apiUrl={API_URL} />
    </div>
  )
}

export default App
